import React, { useState } from 'react';
import * as Styles from './LinkNaBioList.styles';
import {
  Dropdown, Button, IconLink, IconDanger,
  Tooltip,
  IconArrowCurvedRight,
  IconCopy,
  IconAdd,
} from 'gatorcomponents';
import { Modal } from '@/pages/common';
import { copyTextToClipboard } from '@/utils/Application/copyTextToClipboard';
import { commonActions } from '@/redux/modules';
import { useDispatch } from 'react-redux';
import { LINK_NA_BIO_URL } from '@/config';
import withErrorBoundary from '@/utils/errorBoundary';

const LinkNaBioList = ({
  profiles = [],
  locale = {},
  createLinkNaBio = () => null,
  editLinkNaBio = () => null,
  deleteLinkNaBio = () => null,
  testId = 'link-na-bio-list',
  loading = false,
  goToDataAnalysis = () => null,
}) => {
  const [profileToDelete, setProfileToDelete] = useState(undefined);
  const dispatch = useDispatch();

  const handleShowDeleteModal = value => setProfileToDelete(value);

  const showCopyNotification = () => {
    dispatch(commonActions.notifications.set({
      label: locale.copied,
      type: 'success',
    }));
  };

  const handleCopy = (profile) => {
    copyTextToClipboard(`${LINK_NA_BIO_URL}/${profile.username}`);
    showCopyNotification();
  };

  const handleDelete = () => {
    deleteLinkNaBio(profileToDelete.id);
  };

  const dropdownItems = (profile) => {
    const items = [
      {
        id: `${profile.username}-delete`,
        label: locale.deleteShort,
        onClick: () => handleShowDeleteModal(profile),
        icon: <IconDanger />,
      },
    ];

    return items;
  };

  return (
    <Styles.Wrapper data-testid={testId}>
      <Styles.Row isTitle>
        <Styles.TitleWrapper>
          <Styles.Title>
            {locale.myLinks}
          </Styles.Title>

          <Styles.ButtonWrapper>
            {profiles.length <= 4 && (
              <Button
                label={locale.create}
                onClick={() => createLinkNaBio()}
                variant="primary"
                iconLeft={<IconAdd color="highPure" />}
              />
            )}
            {profiles.length > 4 && (
              <Tooltip content={locale.tooltip} width={315}>
                <Button
                  label={locale.create}
                  onClick={() => createLinkNaBio()}
                  disabled
                  iconLeft={<IconAdd color="highPure" />}
                />
              </Tooltip>
            )}
          </Styles.ButtonWrapper>
        </Styles.TitleWrapper>

        <Styles.Description>
          {locale.limit}
        </Styles.Description>
      </Styles.Row>

      <Styles.Profiles>
        {profiles.map((profile, index) => (
          <Styles.ListItem key={profile.id}>
            <Styles.Row>
              <Styles.ProfileLinkWrapper>
                {!profile.is_edit && <Styles.OrangeDot data-testid="orange-dot" />}
                <Styles.ProfileLink
                  href={`${LINK_NA_BIO_URL}/${profile.username}`}
                  target="_blank"
                >
                  <IconLink size="sm" />
                  {`${locale.linkPlaceholder}${profile.username}`}
                </Styles.ProfileLink>

                <Tooltip content={locale.copyShort}>
                  <Styles.CopyIconButton
                    onClick={() => handleCopy(profile)}
                    data-testid="copy-icon-button"
                  >
                    <IconCopy size="sm" />
                  </Styles.CopyIconButton>
                </Tooltip>
              </Styles.ProfileLinkWrapper>

              <Styles.Dropdown>
                <Styles.DropdownWrapper isNewLayout>
                  <Dropdown
                    items={dropdownItems(profile)}
                    position="right"
                  />
                </Styles.DropdownWrapper>
              </Styles.Dropdown>

              {profile.domain && (
                <Styles.DomainLinkedWrapper isMobileComponent>
                  <IconArrowCurvedRight />
                  <Styles.LinkText>
                    {locale.domainConnectedTo}
                    <Styles.DomainLink
                      href={`https://${profile.domain}`}
                      target="_blank"
                    >
                      {profile.domain}
                    </Styles.DomainLink>
                  </Styles.LinkText>
                </Styles.DomainLinkedWrapper>
              )}

              <Styles.ProfileActions>
                <Styles.ButtonWrapper>
                  <Button
                    label={locale.viewData}
                    onClick={() => goToDataAnalysis(profile)}
                    variant="tertiary"
                  />
                </Styles.ButtonWrapper>
                <Styles.ButtonWrapper>
                  <Button
                    label={locale.editPage}
                    onClick={() => editLinkNaBio(profile)}
                    variant="secondary"
                  />
                </Styles.ButtonWrapper>
                <Styles.DropdownWrapper isNewLayout>
                  <Dropdown
                    items={dropdownItems(profile)}
                    position="right"
                  />
                </Styles.DropdownWrapper>
              </Styles.ProfileActions>
              {profile.domain && (
                <Styles.DomainLinkedWrapper>
                  <IconArrowCurvedRight />
                  <Styles.LinkText>
                    {locale.domainConnectedTo}
                    <Styles.DomainLink
                      href={`https://${profile.domain}`}
                      target="_blank"
                    >
                      {profile.domain}
                    </Styles.DomainLink>
                  </Styles.LinkText>
                </Styles.DomainLinkedWrapper>
              )}
            </Styles.Row>
            <Styles.Divider lastRow={index === profiles.length - 1} />
          </Styles.ListItem>
        ))}
      </Styles.Profiles>

      {profileToDelete && (
        <Modal
          maxWidth={620}
          maxHeight={250}
          testId="link-na-bio-modal"
          onClose={handleShowDeleteModal}
        >
          <Styles.ModalContent>
            <Styles.ModalTitle>
              {locale.delete}
            </Styles.ModalTitle>

            <Styles.ModalAlert>
              <IconDanger size="xl" color="attentionPure" />
              {locale.deleteModalDescription(profileToDelete.username)}
            </Styles.ModalAlert>
            <Styles.ModalButtonsWrapper>
              <Styles.ButtonWrapper>
                <Button
                  label={locale.cancel}
                  size="large"
                  variant="tertiary"
                  onClick={() => handleShowDeleteModal()}
                />
              </Styles.ButtonWrapper>

              <Styles.ButtonWrapper>
                <Button
                  label={locale.delete}
                  size="large"
                  onClick={handleDelete}
                  loading={loading}
                  testId="modal-delete-button"
                />
              </Styles.ButtonWrapper>
            </Styles.ModalButtonsWrapper>

          </Styles.ModalContent>
        </Modal>
      )}
    </Styles.Wrapper>
  );
};

export default withErrorBoundary(LinkNaBioList, 'LinkNaBioList');
